import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { bookingsService } from '../services/api';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { 
  getAccommodationDisplay, 
  getPrimaryCourse,
  getPropertyName 
} from '../utils/bookingHelpers';

function BookingCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBookings();
  }, [currentDate]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      
      const response = await bookingsService.getAll({
        startDate: monthStart.toISOString(),
        endDate: monthEnd.toISOString()
      });
      
      if (response && response.data && response.data.bookings) {
        setBookings(response.data.bookings);
      } else if (Array.isArray(response)) {
        setBookings(response);
      } else {
        setBookings([]);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast.error('Failed to load bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const nextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const prevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const getBookingsForDate = (date) => {
    return bookings.filter(booking => {
      const checkIn = new Date(booking.checkIn);
      const checkOut = new Date(booking.checkOut);
      return date >= checkIn && date <= checkOut;
    });
  };

  const getBookingIcon = (booking) => {
    switch (booking.status) {
      case 'CONFIRMED': return '';
      case 'PENDING': return '';
      case 'CANCELLED': return '';
      case 'COMPLETED': return '';
      default: return '';
    }
  };

  const getBookingColor = (booking) => {
    switch (booking.status) {
      case 'CONFIRMED': return '#163626';
      case 'PENDING': return '#C9903C';
      case 'CANCELLED': return '#8E3628';
      case 'COMPLETED': return '#5F6B61';
      default: return '#1A3E2A';
    }
  };

  const renderCalendarHeader = () => {
    const dateFormat = 'MMMM yyyy';
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '20px 0',
        borderBottom: '2px solid #E3DFD3'
      }}>
        <button
          onClick={prevMonth}
          style={{
            backgroundColor: '#1A3E2A',
            color: 'white',
            border: 'none',
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          ← Previous
        </button>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
          {format(currentDate, dateFormat)}
        </h2>
        <button
          onClick={nextMonth}
          style={{
            backgroundColor: '#1A3E2A',
            color: 'white',
            border: 'none',
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Next →
        </button>
      </div>
    );
  };

  const renderDaysOfWeek = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', backgroundColor: '#E3DFD3' }}>
        {days.map(day => (
          <div
            key={day}
            style={{
              backgroundColor: '#F5F3EB',
              padding: '10px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            {day}
          </div>
        ))}
      </div>
    );
  };

  const renderCalendarDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const dayBookings = getBookingsForDate(day);
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isToday = isSameDay(day, new Date());

        days.push(
          <div
            key={day}
            style={{
              backgroundColor: isCurrentMonth ? 'white' : '#F5F3EB',
              border: isToday ? '2px solid #1A3E2A' : '1px solid #E3DFD3',
              padding: '8px',
              minHeight: '100px',
              position: 'relative',
              opacity: isCurrentMonth ? 1 : 0.6
            }}
          >
            <div style={{ 
              fontWeight: isToday ? 'bold' : 'normal',
              marginBottom: '5px',
              fontSize: '14px'
            }}>
              {format(day, 'd')}
            </div>
            
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '2px',
              maxHeight: '60px',
              overflowY: 'auto'
            }}>
              {dayBookings.map((booking, index) => (
                <span
                  key={`${booking.id}-${index}`}
                  onClick={() => setSelectedBooking(booking)}
                  style={{
                    display: 'inline-block',
                    fontSize: '12px',
                    cursor: 'pointer',
                    backgroundColor: getBookingColor(booking),
                    color: 'white',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    margin: '1px'
                  }}
                  title={`${booking.user?.name || booking.user?.email || 'Guest'} - ${getAccommodationDisplay(booking)}`}
                >
                  {getBookingIcon(booking)}
                </span>
              ))}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
          {days}
        </div>
      );
      days = [];
    }

    return <div>{rows}</div>;
  };

  const renderBookingModal = () => {
    if (!selectedBooking) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
        onClick={() => setSelectedBooking(null)}
      >
        <div
          style={{
            backgroundColor: '#E9DFCE',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: '#24332A' }}>Booking Details</h3>
            <button
              onClick={() => setSelectedBooking(null)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#5F6B61'
              }}
            >
              ×
            </button>
          </div>

          <div style={{ lineHeight: '1.6' }}>
            <div style={{ marginBottom: '15px' }}>
              <strong>Booking ID:</strong> {selectedBooking.id.slice(-8)}
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <strong>Status:</strong>
              <span
                style={{
                  marginLeft: '8px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: getBookingColor(selectedBooking),
                  color: 'white',
                  fontSize: '12px'
                }}
              >
                {getBookingIcon(selectedBooking)} {selectedBooking.status}
              </span>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Guest:</strong> {selectedBooking.user?.name || selectedBooking.user?.email || 'Guest'}
            </div>

            {selectedBooking.user?.email && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Email:</strong> {selectedBooking.user.email}
              </div>
            )}

            <div style={{ marginBottom: '15px' }}>
              <strong>Accommodation:</strong> {getAccommodationDisplay(selectedBooking)}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Check-in:</strong> {format(new Date(selectedBooking.checkIn), 'MMM dd, yyyy')}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Check-out:</strong> {format(new Date(selectedBooking.checkOut), 'MMM dd, yyyy')}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Total Price:</strong> ₹{selectedBooking.totalPrice}
            </div>

            {(() => {
              const course = getPrimaryCourse(selectedBooking);
              return course ? (
                <div style={{ marginBottom: '15px' }}>
                  <strong>Course:</strong> {course.name}
                </div>
              ) : null;
            })()}

            <div style={{ marginBottom: '15px' }}>
              <strong>Property:</strong> {getPropertyName(selectedBooking)}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Created:</strong> {format(new Date(selectedBooking.createdAt), 'MMM dd, yyyy HH:mm')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: 0, marginBottom: '10px' }}>Booking Calendar</h2>
        <p style={{ color: '#5F6B61', margin: 0 }}>
          Click on booking icons to view details. Icons: Confirmed, Pending, Cancelled, Completed
        </p>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading bookings...
        </div>
      )}

      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        {renderCalendarHeader()}
        {renderDaysOfWeek()}
        {renderCalendarDays()}
      </div>

      {renderBookingModal()}
    </div>
  );
}

export default BookingCalendar;